import Image from '@eliadmin/core/src/components/image/image-wrapped';
import Link from '@eliadmin/core/src/components/link';

import type { GeneralLayout } from '@eliadmin/core/src/api/general/types/general-layout-types';

import { cn } from '@eliadmin/core/src/utils/style-utils';
import { Heading } from '../ui/heading';
import { HtmlRendererRaw } from '@eliadmin/core/src/components/ui/html-renderer-raw';

export function Contact({
  subtitle,
  title,
  generalLayout,
}: {
  subtitle: string;
  title: string;
  generalLayout: GeneralLayout['data'];
}) {
  return (
    <section
      id="kontakt"
      className={cn(
        'custom-section',
        'gap-8 pb-8 pt-8 sm:gap-10 sm:pb-12 sm:pt-12 lg:gap-12 lg:pb-24 lg:pt-[60px] xl:pb-[120px]',
      )}
    >
      <Heading
        subtitle={subtitle}
        title={title}
        className={{ container: 'items-center justify-center text-center' }}
      />
      <div
        className={cn(
          'custom-container',
          'flex flex-col items-start justify-start gap-4 sm:gap-5 lg:gap-6',
        )}
      >
        <div className="grid w-full grid-cols-1 gap-4 sm:gap-5 md:grid-cols-2 lg:gap-6">
          <ContactCard
            label="Napíšte nám e-mail"
            linkText="Poslať e-mail"
            url={`mailto:${generalLayout.contact?.email}`}
            value={generalLayout.contact?.email || ''}
            icon="/images/email.svg"
          />
          <ContactCard
            label="Zavolajte nám"
            linkText="Zavolať"
            url={`tel:${generalLayout.contact?.phone}`}
            value={generalLayout.contact?.phone || ''}
            icon="/images/call.svg"
          />
        </div>
        <div className="grid w-full grid-cols-1 gap-4 sm:gap-5 md:grid-cols-2 lg:gap-6">
          <div className="col-span-1 flex w-full flex-col items-start justify-start gap-4 sm:gap-5 lg:gap-6">
            <ContactCard
              label="Kde nás nájdete?"
              linkText="Navigovať"
              url={
                'https://www.google.com/maps/place/Tom%C3%A1%C5%A1ikova+8a,+080+01+Pre%C5%A1ov/@49.0170718,21.2249787,205m/data=!3m2!1e3!4b1!4m6!3m5!1s0x473ef2b911743a0b:0xcdc71582d35c4ee2!8m2!3d49.0170718!4d21.2256238!16s%2Fg%2F11pw3gvxwj?entry=ttu&g_ep=EgoyMDI1MTEyMy4xIKXMDSoASAFQAw%3D%3D'
              }
              value={`${generalLayout.contact?.address},<br>${generalLayout.contact?.postcode} Sídlisko III (OC Centrum)`}
              icon="/images/pin.svg"
            />
            <ContactCard
              label="Otváracie hodiny"
              value={'Po - Pia: 9:00 - 18:00<br/>So - Ne: Zatvorené'}
              icon="/images/clock.svg"
            />
          </div>
          <iframe
            title="Mapa"
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2624.0219887873255!2d21.223454215833204!3d49.01721049714237!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x473ef3586aa92bf3%3A0xbb1d74b64cdf4adb!2sKADERN%C3%8DCTVO%20KAKADU%20-%20Detsk%C3%A9%2C%20d%C3%A1mske%20a%20p%C3%A1nske%20kadern%C3%ADctvo!5e0!3m2!1sen!2ssk!4v170XXXXXXXXXXXXXX"
            className="col-span-1 h-[200px] w-full rounded-[24px] sm:h-full"
            style={{ border: 0 }}
          />
        </div>
      </div>
    </section>
  );
}

interface ContactCardProps {
  label: string;
  value: string;
  icon: string;
  url?: string;
  linkText?: string;
}

export function ContactCard({ label, value, icon, url, linkText }: ContactCardProps) {
  return (
    <div
      className={cn(
        'col-span-1 flex w-full flex-col items-start justify-start gap-4 rounded-[24px] border border-black/10 p-4 sm:gap-5 sm:p-5 lg:gap-6 lg:p-6',
      )}
    >
      <div className="flex w-full items-center justify-between">
        <p className="text-sm font-medium text-black sm:text-base">{label}</p>
        {url && linkText && (
          <Link
            prefetch={false}
            href={url}
            target="_blank"
            className={cn(
              'relative flex items-center justify-center gap-2 transition-all hover:opacity-80',
              'ea-contact-card__link',
            )}
          >
            {linkText}
            <Image
              src={'/images/arrow-right.svg'}
              alt=""
              width={20}
              height={20}
              className="max-h-5 min-h-5 min-w-5 max-w-5 object-contain"
            />
          </Link>
        )}
      </div>
      <div className="flex items-center justify-start gap-4">
        <div className="flex max-h-12 min-h-12 min-w-12 max-w-12 items-center justify-center rounded-full bg-primary-foreground sm:max-h-14 sm:min-h-14 sm:min-w-14 sm:max-w-14">
          <Image
            src={icon}
            alt={value}
            width={24}
            height={24}
            className="max-h-6 min-h-6 min-w-6 max-w-6 object-contain"
          />
        </div>
        <HtmlRendererRaw
          as="p"
          className={cn('w-full text-base font-bold sm:text-lg lg:text-xl')}
          html={value}
        />
      </div>
    </div>
  );
}
